import type { Device } from '../../api/types.ts';

function lastSeenMs(value: Device['last_seen']): number | null {
  if (value === undefined || value === null || value === '') return null;
  const ms = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isFinite(ms) ? ms : null;
}

export function formatLastSeen(value: Device['last_seen']): string {
  const ms = lastSeenMs(value);
  return ms === null ? 'Unknown' : new Date(ms).toLocaleString();
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Seen in the last quarter of an hour: the app is open on it now, or as good as. */
export const ACTIVE_WINDOW_MS = 15 * MINUTE;

export function isActive(value: Device['last_seen'], now: number = Date.now()): boolean {
  const ms = lastSeenMs(value);
  return ms !== null && now - ms < ACTIVE_WINDOW_MS;
}

/** "Active now", "3 hours ago", "Yesterday", "12 days ago", then a date. */
export function relativeLastSeen(value: Device['last_seen'], now: number = Date.now()): string {
  const ms = lastSeenMs(value);
  if (ms === null) return 'Never seen';
  const ago = Math.max(0, now - ms);
  if (ago < ACTIVE_WINDOW_MS) return 'Active now';
  if (ago < HOUR) return `${Math.round(ago / MINUTE)} minutes ago`;
  if (ago < DAY) return `${Math.round(ago / HOUR)} ${Math.round(ago / HOUR) === 1 ? 'hour' : 'hours'} ago`;
  if (ago < 2 * DAY) return 'Yesterday';
  if (ago < 45 * DAY) return `${Math.round(ago / DAY)} days ago`;
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export type DeviceKind = 'phone' | 'tablet' | 'laptop' | 'desktop' | 'other';

/** A guess at the kind of device from its model name, for the icon only. */
export function deviceKind(device: Pick<Device, 'd_model' | 'd_manufacturer'>): DeviceKind {
  const name = `${device.d_model ?? ''} ${device.d_manufacturer ?? ''}`.toLowerCase();
  if (/ipad|tablet|galaxy tab|\btab\b/.test(name)) return 'tablet';
  if (/iphone|pixel|galaxy|phone|android|oneplus|xiaomi|redmi|huawei|motorola|moto |nokia|oppo|vivo/.test(name)) return 'phone';
  if (/macbook|laptop|notebook|thinkpad|surface/.test(name)) return 'laptop';
  if (/imac|mac mini|mac studio|windows|desktop|pc|linux|mac/.test(name)) return 'desktop';
  return 'other';
}

/** Most recently seen first; never-seen devices last. */
export function byLastSeen(devices: Device[]): Device[] {
  return [...devices].sort((a, b) => (lastSeenMs(b.last_seen) ?? -1) - (lastSeenMs(a.last_seen) ?? -1));
}
